import React, { useState, useEffect } from 'react';
import { Smartphone, Monitor, Tablet, Globe, Clock, MapPin, Trash2, LogOut, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import api from '../utils/api';

const SessionManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [revoking, setRevoking] = useState(null);
  const [showSensitiveModal, setShowSensitiveModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);

  const currentSessionId = localStorage.getItem('session_id');

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/sessions');
      setSessions(response.data.sessions || []);
    } catch (err) {
      setError('Failed to load sessions');
      console.error('Load sessions error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="w-5 h-5" />;
      case 'tablet':
        return <Tablet className="w-5 h-5" />;
      default:
        return <Monitor className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    // Less than a minute
    if (diff < 60000) return 'Just now';
    // Less than an hour
    if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`;
    // Less than a day
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    // Less than a week
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const handleRevokeSession = (sessionId) => {
    setPendingAction({ type: 'revoke_session', sessionId });
    setShowSensitiveModal(true);
  };

  const handleRevokeAllSessions = () => {
    setPendingAction({ type: 'revoke_all_sessions' });
    setShowSensitiveModal(true);
  };

  const verifySensitiveAction = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      // Get sensitive action token
      const actionType = pendingAction.type === 'revoke_all_sessions' ? 'revoke_all_sessions' : 'revoke_session';
      const verifyResponse = await api.post('/auth/verify-sensitive-action', {
        action: actionType,
        password: password
      });

      if (!verifyResponse.data.success) {
        if (verifyResponse.data.mfa_required) {
          setError('MFA verification required. Please try again.');
          // TODO: Handle MFA requirement
          return;
        }
        setError('Verification failed');
        return;
      }

      const sensitiveToken = verifyResponse.data.sensitive_action_token;

      // Perform the action
      if (pendingAction.type === 'revoke_session') {
        await api.delete(`/auth/sessions/${pendingAction.sessionId}`, {
          headers: { 'X-Sensitive-Action-Token': sensitiveToken }
        });
        setSuccess('Session revoked successfully');
        setSessions(sessions.filter(s => s.id !== pendingAction.sessionId));
      } else if (pendingAction.type === 'revoke_all_sessions') {
        await api.delete('/auth/sessions', {
          headers: {
            'X-Sensitive-Action-Token': sensitiveToken,
            'X-Current-Session-Id': currentSessionId
          }
        });
        setSuccess('All other sessions have been signed out');
        // Keep only current session
        setSessions(sessions.filter(s => s.id === currentSessionId));
      }

      // Close modal and reset
      setShowSensitiveModal(false);
      setPendingAction(null);
      setPassword('');

    } catch (err) {
      setError(err.response?.data?.detail || 'Action failed. Please check your password.');
    } finally {
      setVerifying(false);
    }
  };

  const closeSensitiveModal = () => {
    setShowSensitiveModal(false);
    setPendingAction(null);
    setPassword('');
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Active Sessions
              </CardTitle>
              <CardDescription>
                Manage devices where you're currently signed in
              </CardDescription>
            </div>
            {sessions.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevokeAllSessions}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out all other devices
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-4 border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">{success}</AlertDescription>
            </Alert>
          )}

          {sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Monitor className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No active sessions found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    session.id === currentSessionId
                      ? 'border-purple-200 bg-purple-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${
                      session.id === currentSessionId ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getDeviceIcon(session.device_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {session.device_name || 'Unknown Device'}
                        </span>
                        {session.id === currentSessionId && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            Current session
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(session.last_used_at)}
                        </span>
                        {session.ip_address && (
                          <span className="flex items-center gap-1">
                            <Globe className="w-3.5 h-3.5" />
                            {session.ip_address}
                          </span>
                        )}
                        {session.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {session.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {session.id !== currentSessionId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={revoking === session.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {revoking === session.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1" />
                          Revoke
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">About sessions</h4>
            <p className="text-sm text-gray-600">
              When you check "Stay signed in for 30 days" during login, a session is created for that device. 
              If you notice any sessions you don't recognize, revoke them immediately and consider changing your password.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Password Verification Modal */}
      {showSensitiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm your identity
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {pendingAction?.type === 'revoke_all_sessions'
                ? 'Enter your password to sign out of all other devices.'
                : 'Enter your password to revoke this session.'}
            </p>
            
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verifySensitiveAction()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-4"
              autoFocus
            />

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeSensitiveModal}
                disabled={verifying}
              >
                Cancel
              </Button>
              <Button
                onClick={verifySensitiveAction}
                disabled={verifying || !password}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {verifying ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Verifying...
                  </div>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionManagement;
